import React, { useState, useEffect, useRef } from "react";
import { FaPlus, FaEdit, FaTrash, FaFolderOpen, FaExternalLinkAlt, FaUpload, FaPaperclip } from "react-icons/fa";
import AdminModal from "../../components/AdminModal";
import LoadingState from "../../components/LoadingState";
import ReglamentosEdificio from "../../components/ReglamentosEdificio";
import { colors, radius, spacing, typography, cardBase, btnPrimary, btnSecondary, inputBase } from "../../utils/designTokens";
import { useBuilding } from "../../context/BuildingContext";
import { db, storage } from "../../firebase";
import { useToast } from "../../components/Toast";
import { VISIBILITY_OPTIONS, DOC_VISIBILITY, normalizeVisibility, visibilityLabel } from "../../utils/docVisibility";
import { normalizeDocType, docTypeKey, docTypeOptions, DEFAULT_DOC_TYPE } from "../../utils/docTypes";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

/**
 * Documentos del edificio — ÚNICO lugar donde el admin gestiona documentos.
 *  - Reglamentos (copropiedad + interno): campos del doc building, Habi los
 *    indexa (ver ReglamentosEdificio). Antes estaban en Editar Edificio.
 *  - Resto de documentos: colección `buildingdocs`, archivo en Storage
 *    `buildings/{buildingDocId}/documentos/`, con visibilidad por rol.
 * Lo consumen la pantalla del residente (DocumentacionEdificio) y Habi
 * (consultar_documentos_edificio), que leen description/type/date/fileUrl/visibilidad.
 */

// Tipos de documento: lista compartida con la vista del residente (utils/docTypes).
// Ayuda corta por tipo bajo el select, para que el admin sepa dónde va cada cosa.
const TYPE_HINTS = {
  seguros: "Pólizas, certificados y renovaciones de seguros del edificio.",
  bps: "Constancias, planillas y aportes del BPS del personal del edificio.",
  intendencia: "Tributos, permisos, habilitaciones y trámites municipales.",
  reglamento: "El reglamento de copropiedad y el interno van en la sección \"Reglamentos\" de arriba (así Habi los lee). Usá este tipo para otros reglamentos.",
  actas: "Actas de asambleas de copropietarios.",
  manuales: "Manuales de uso de equipos e instalaciones (ascensor, bombas, portero, etc.).",
  otros: "Cualquier documento que no encaje en las otras categorías.",
};

// Mismos tipos que permite storage.rules (PDF, imágenes, Excel) y mismo tope.
const ACCEPT = "application/pdf,image/*,.xlsx,.xls";
const MAX_BYTES = 10 * 1024 * 1024;
const isAllowedFile = (f) =>
  f.type === "application/pdf" ||
  f.type.startsWith("image/") ||
  f.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
  f.type === "application/vnd.ms-excel";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// El nombre/título se guarda en `description`: lo leen la pantalla del
// residente y Habi, así no hace falta migrar nada.
const emptyForm = () => ({
  description: "",
  date: todayISO(),
  type: DEFAULT_DOC_TYPE,
  visibilidad: DOC_VISIBILITY.TODOS,
  file: null,
});

const DocumentacionAdmin = ({ setSelected }) => {
  const { buildings, selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const fileInputRef = useRef(null);

  const buildingsToProcess = selectedBuilding
    ? buildings.filter((b) => b.buildingid === selectedBuilding.buildingid)
    : buildings;

  const fetchItems = async () => {
    if (!buildings || buildings.length === 0) { setLoading(false); return; }
    setLoading(true);
    try {
      let all = [];
      for (const b of buildingsToProcess) {
        const q = query(
          collection(db, "buildingdocs"),
          where("buildingid", "==", Number(b.buildingid))
        );
        const snap = await getDocs(q);
        const data = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          buildingName: b.name,
        }));
        all = all.concat(data);
      }
      all.sort((a, b) => (b.date?.seconds || 0) - (a.date?.seconds || 0));
      setItems(all);
    } catch (error) {
      console.error("Error al traer documentación:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBuilding, buildings]);

  const handleOpenCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditing(item);
    const d = item.date?.seconds
      ? new Date(item.date.seconds * 1000).toISOString().slice(0, 10)
      : "";
    setForm({
      description: item.description || "",
      date: d,
      type: normalizeDocType(item.type),
      visibilidad: normalizeVisibility(item.visibilidad),
      file: null,
    });
    setShowModal(true);
  };

  const handlePickFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // permitir re-elegir el mismo archivo
    if (!f) return;
    if (!isAllowedFile(f)) { showToast("Formato no permitido. Subí un PDF, una imagen o un Excel.", "error"); return; }
    if (f.size > MAX_BYTES) { showToast("El archivo no puede superar los 10MB", "error"); return; }
    setForm((prev) => ({
      ...prev,
      file: f,
      // Si todavía no puso nombre, sugerimos el del archivo (sin extensión).
      description: prev.description || f.name.replace(/\.[^.]+$/, ""),
    }));
  };

  const handleSave = async () => {
    if (!selectedBuilding) { showToast("Selecciona un edificio primero", "warning"); return; }
    if (!selectedBuilding.id) { showToast("No se pudo identificar el edificio. Recargá la pantalla.", "error"); return; }
    if (!form.description.trim()) { showToast("El nombre del documento es obligatorio", "error"); return; }
    if (!form.date) { showToast("La fecha es obligatoria", "error"); return; }
    if (!editing && !form.file) { showToast("Adjuntá el archivo del documento", "error"); return; }

    setSaving(true);
    try {
      const payload = {
        buildingid: Number(selectedBuilding.buildingid),
        description: form.description.trim(),
        date: Timestamp.fromDate(new Date(form.date + "T12:00:00")),
        type: form.type,
        visibilidad: normalizeVisibility(form.visibilidad),
        updatedAt: serverTimestamp(),
      };

      let oldStoragePath = null;
      if (form.file) {
        const safe = form.file.name.replace(/[^\w.-]+/g, "_").slice(-80);
        const path = `buildings/${selectedBuilding.id}/documentos/${Date.now()}_${safe}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, form.file, { contentType: form.file.type });
        payload.fileUrl = await getDownloadURL(fileRef);
        payload.fileName = form.file.name;
        payload.storagePath = path;
        payload.contentType = form.file.type;
        if (editing?.storagePath) oldStoragePath = editing.storagePath;
      }

      if (editing) {
        await updateDoc(doc(db, "buildingdocs", editing.id), payload);
      } else {
        await addDoc(collection(db, "buildingdocs"), { ...payload, createdAt: serverTimestamp() });
      }

      // Reemplazo de archivo: borrar el anterior recién después de guardar OK.
      if (oldStoragePath) {
        deleteObject(storageRef(storage, oldStoragePath)).catch((e) =>
          console.warn("No se pudo borrar el archivo anterior:", e)
        );
      }

      showToast(editing ? "Documento actualizado" : "Documento cargado", "success");
      setShowModal(false);
      setForm(emptyForm());
      setEditing(null);
      fetchItems();
    } catch (error) {
      console.error("Error al guardar documento:", error);
      showToast("Error al guardar: " + error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      await deleteDoc(doc(db, "buildingdocs", item.id));
      if (item.storagePath) {
        deleteObject(storageRef(storage, item.storagePath)).catch((e) =>
          console.warn("No se pudo borrar el archivo de Storage:", e)
        );
      }
      setDeleteConfirm(null);
      fetchItems();
    } catch (error) {
      console.error("Error al eliminar documento:", error);
      showToast("Error al eliminar: " + error.message, "error");
    }
  };

  const toDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString("es-UY", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  // Colores de badge según tipo de documento
  const typeColor = (type) => {
    switch (docTypeKey(type)) {
      case "seguros": return { bg: colors.infoLight, color: colors.infoDark };
      case "bps":
      case "intendencia":
      case "factura": return { bg: colors.warningLight, color: colors.warningDark };
      case "reglamento":
      case "actas": return { bg: colors.successLight, color: colors.successDark };
      default: return { bg: colors.gray100, color: colors.gray900 };
    }
  };

  const currentFileName = form.file?.name || editing?.fileName || (editing?.fileUrl ? "Archivo actual" : "");

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <div style={styles.headerTitleSection}>
          <h2 style={styles.pageTitle}>
            <FaFolderOpen style={{ marginRight: 8 }} />
            Documentación
          </h2>
          <p style={styles.pageSubtitle}>
            {selectedBuilding
              ? `${selectedBuilding.name} · ${items.length} documentos`
              : `${buildingsToProcess.length} edificio${buildingsToProcess.length > 1 ? "s" : ""} · ${items.length} documentos`}
          </p>
        </div>
        <button
          style={{
            ...styles.addBtn,
            opacity: !selectedBuilding ? 0.5 : 1,
            cursor: !selectedBuilding ? "not-allowed" : "pointer",
          }}
          disabled={!selectedBuilding}
          onClick={handleOpenCreate}
        >
          <FaPlus size={14} /> Nuevo Documento
        </button>
      </div>

      {!selectedBuilding && (
        <div style={styles.selectBuildingNotice}>
          <p>Selecciona un edificio desde el filtro superior para ver y cargar documentos.</p>
        </div>
      )}

      {selectedBuilding?.id && <ReglamentosEdificio buildingDocId={selectedBuilding.id} />}

      {selectedBuilding && <h3 style={styles.sectionTitle}>Otros documentos</h3>}

      {loading && selectedBuilding ? (
        <LoadingState message="Cargando documentos…" />
      ) : selectedBuilding && items.length === 0 ? (
        <div style={styles.emptyState}>
          <FaFolderOpen size={40} color={colors.gray300} />
          <p style={styles.emptyText}>No hay otros documentos para este edificio.</p>
          <button style={styles.emptyBtn} onClick={handleOpenCreate}>
            <FaPlus size={12} /> Cargar primer documento
          </button>
        </div>
      ) : selectedBuilding ? (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Nombre</th>
                <th style={styles.th}>Tipo</th>
                <th style={styles.th}>Visible para</th>
                <th style={styles.th}>Fecha</th>
                <th style={styles.th}>Archivo</th>
                <th style={styles.th}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const tipo = normalizeDocType(item.type);
                const tc = typeColor(tipo);
                return (
                  <tr key={item.id} style={styles.tr}>
                    <td style={styles.td}>{item.description}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: tc.bg, color: tc.color }}>
                        {tipo}
                      </span>
                    </td>
                    <td style={{ ...styles.td, color: normalizeVisibility(item.visibilidad) === DOC_VISIBILITY.NINGUNO ? colors.gray600 : colors.black }}>
                      {visibilityLabel(item.visibilidad)}
                    </td>
                    <td style={styles.td}>{toDate(item.date)}</td>
                    <td style={styles.td}>
                      {item.fileUrl ? (
                        <a
                          href={item.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: colors.primary, display: "flex", alignItems: "center", gap: 4 }}
                          title={item.fileName || "Ver archivo"}
                        >
                          <FaExternalLinkAlt size={12} /> Ver
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        <button style={styles.iconBtn} onClick={() => handleOpenEdit(item)} title="Editar" aria-label="Editar documento">
                          <FaEdit color={colors.primary} />
                        </button>
                        {deleteConfirm === item.id ? (
                          <div style={styles.confirmDelete}>
                            <span style={{ fontSize: typography.sizes.sm, color: colors.errorDark }}>¿Eliminar?</span>
                            <button style={styles.confirmYes} onClick={() => handleDelete(item)}>Sí</button>
                            <button style={styles.confirmNo} onClick={() => setDeleteConfirm(null)}>No</button>
                          </div>
                        ) : (
                          <button style={styles.iconBtn} onClick={() => setDeleteConfirm(item.id)} title="Eliminar" aria-label="Eliminar documento">
                            <FaTrash color={colors.errorDark} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      <AdminModal
        open={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Editar Documento" : "Nuevo Documento"}
        size="medium"
        footer={
          <>
            <button style={styles.cancelBtn} onClick={() => setShowModal(false)} disabled={saving}>
              Cancelar
            </button>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>
              {saving ? "Guardando..." : editing ? "Actualizar" : "Cargar"}
            </button>
          </>
        }
      >
        <div style={styles.buildingInfo}>
          <span style={styles.buildingLabel}>Edificio:</span>
          <span style={styles.buildingName}>{selectedBuilding?.name}</span>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label}>Archivo {editing ? "" : "*"}</label>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            style={{ display: "none" }}
            onChange={handlePickFile}
            aria-label="Elegir archivo del documento"
          />
          <div style={styles.fileBox}>
            <FaPaperclip color={currentFileName ? colors.primary : colors.gray600} />
            <span style={{ ...styles.fileName, color: currentFileName ? colors.gray900 : colors.gray600 }}>
              {currentFileName || "Ningún archivo seleccionado"}
            </span>
            <button type="button" style={styles.fileBtn} onClick={() => fileInputRef.current?.click()} disabled={saving}>
              <FaUpload size={12} /> {currentFileName ? "Cambiar" : "Elegir archivo"}
            </button>
          </div>
          <p style={styles.hint}>PDF, imagen o Excel, hasta 10MB.</p>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="doc-nombre">Nombre del documento *</label>
          <input
            id="doc-nombre"
            type="text"
            style={styles.input}
            placeholder="Ej: Acta de asamblea – Septiembre 2026, Seguro del edificio"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            required
            aria-required="true"
          />
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="doc-tipo">Tipo</label>
          <select
            id="doc-tipo"
            style={styles.input}
            value={form.type}
            onChange={(e) => setForm({ ...form, type: e.target.value })}
          >
            {docTypeOptions(editing?.type).map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
          {TYPE_HINTS[docTypeKey(form.type)] && (
            <p style={styles.hint}>{TYPE_HINTS[docTypeKey(form.type)]}</p>
          )}
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="doc-visibilidad">¿Quién puede verlo? *</label>
          <select
            id="doc-visibilidad"
            style={styles.input}
            value={form.visibilidad}
            onChange={(e) => setForm({ ...form, visibilidad: e.target.value })}
          >
            {VISIBILITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p style={styles.hint}>
            Lo que ve el inquilino también lo ve el propietario. "Ninguno" lo deja
            solo para la administración.
          </p>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="doc-fecha">Fecha *</label>
          <input
            id="doc-fecha"
            type="date"
            style={styles.input}
            value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })}
          />
        </div>
      </AdminModal>
    </div>
  );
};

// Estilos con design tokens
const styles = {
  page: {
    padding: spacing[6],
    backgroundColor: colors.bgMain,
    minHeight: "100vh",
    fontFamily: typography.fontFamily,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    fontFamily: typography.fontFamily,
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.primary,
    margin: 0,
    letterSpacing: "-0.01em",
    display: "flex",
    alignItems: "center",
  },
  pageSubtitle: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
  },
  addBtn: {
    ...btnPrimary,
  },
  selectBuildingNotice: {
    ...cardBase,
    padding: 40,
    textAlign: "center",
    color: colors.gray600,
    fontSize: typography.sizes.md,
  },
  emptyState: {
    ...cardBase,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 60,
    gap: spacing[3],
  },
  emptyText: {
    color: colors.gray600,
    fontSize: typography.sizes.md,
    margin: 0,
  },
  emptyBtn: {
    ...btnPrimary,
    marginTop: spacing[2],
  },
  tableWrapper: {
    ...cardBase,
    padding: 0,
    overflow: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    textAlign: "left",
    padding: "12px 16px",
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray600,
    borderBottom: `1px solid ${colors.gray200}`,
    background: colors.gray50,
  },
  tr: {
    borderBottom: `1px solid ${colors.gray100}`,
  },
  td: {
    padding: "12px 16px",
    fontSize: typography.sizes.md,
    color: colors.black,
  },
  badge: {
    padding: "4px 10px",
    borderRadius: radius.full,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  iconBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: radius.sm,
  },
  confirmDelete: {
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  confirmYes: {
    background: colors.error,
    color: colors.white,
    border: "none",
    borderRadius: radius.sm,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
  },
  confirmNo: {
    background: colors.gray200,
    color: colors.gray900,
    border: "none",
    borderRadius: radius.sm,
    padding: "4px 10px",
    fontSize: typography.sizes.sm,
    fontWeight: 400,
    cursor: "pointer",
  },
  buildingInfo: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    marginBottom: spacing[5],
    padding: "12px 16px",
    background: colors.primarySubtle,
    borderRadius: radius.sm,
  },
  buildingLabel: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
  },
  buildingName: {
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.primary,
  },
  formGroup: {
    marginBottom: spacing[4],
  },
  label: {
    display: "block",
    fontSize: typography.sizes.md,
    fontWeight: 400,
    color: colors.gray900,
    marginBottom: 6,
  },
  input: {
    ...inputBase,
    boxSizing: "border-box",
  },
  hint: {
    margin: `${spacing[1]}px 0 0`,
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  sectionTitle: {
    margin: `${spacing[2]}px 0 ${spacing[3]}px`,
    fontSize: typography.sizes.lg,
    fontWeight: 400,
    color: colors.gray900,
  },
  fileBox: {
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    padding: "10px 12px",
    border: `1px dashed ${colors.gray300}`,
    borderRadius: radius.sm,
    background: colors.gray50,
  },
  fileName: {
    flex: 1,
    minWidth: 0,
    fontSize: typography.sizes.md,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileBtn: {
    ...btnSecondary,
    padding: "6px 12px",
    fontSize: typography.sizes.base,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },
  cancelBtn: {
    ...btnSecondary,
  },
  saveBtn: {
    ...btnPrimary,
  },
};

export default DocumentacionAdmin;
